/**
 * CORE MODULE
 */
import { Http, HttpModule } from '@angular/http';
import { HttpClientModule, HttpClient } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { BrowserModule } from '@angular/platform-browser';

/**
 * INSTALLED MODULES
 */
import { PopoverModule } from 'ngx-bootstrap/popover';
import { ClipboardModule } from 'ngx-clipboard';
import { SimpleNotificationsModule } from 'angular2-notifications';
import { ButtonsModule, DatepickerModule, TabsModule, BsDropdownModule } from 'ngx-bootstrap';
import { SelectModule } from 'ng2-select';
import { TextMaskModule } from 'angular2-text-mask';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgxStripeModule } from 'ngx-stripe';
import { environment } from '../environments/environment';
import { ChartsModule } from 'ng4-charts/ng4-charts';

/**
 * CUSTOM COMPONENT, SERVICES ETC.
 */
import { AboutComponent } from './custom/about/about.component';
import { ActivityComponent } from './custom/activity/activity.component';
import { AllTreatmentsComponent } from './custom/all-treatments/all-treatments.component';
import { AppComponent } from './app.component';
import { AppMemoryService } from './core/app-memory.service';
import { AppRoutingModule } from './app-routing.module';
import { AuthenticatedHttpService } from './auth/auth-http.service';
import { CatComponent } from './custom/cat/cat.component';
import { ContactComponent } from './custom/contact/contact.component';
import { DateObjPipe } from './core/pipes/date-obj.pipe';
import { AgePipe } from './core/pipes/age.pipe';
import { ForgotComponent } from './auth/forgot/forgot.component';
import { GetService } from './core/get.service';
import { HeaderComponent } from './custom/header/header.component';
import { HeaderXsComponent } from './custom/header-xs/header-xs.component';
import { HorseComponent } from './custom/horse/horse.component';
import { HorsesComponent } from './custom/horses/horses.component';
import { ImageUploadModule } from './core/angular2-image-upload/image-upload.module';
import { LoaderComponent } from './core/loader/loader.component';
import { LoginComponent } from './auth/login/login.component';
import { LoginRoutingModule } from './auth/auth-routing.module';
import { NavComponent } from './custom/nav/nav.component';
import { NoopAnimationsModule } from '@angular/platform-browser/animations';
import { PaginationComponent } from './core/pagination/pagination.component';
import { ProfileComponent } from './custom/profile/profile.component';
import { RouteLoggedComponent } from './core/route-logged/route-logged.component';
import { RuleBookComponent } from './custom/rule-book/rule-book.component';
import { YardsComponent } from './custom/yards/yards.component';
import { RulesUnauthComponent } from './custom/rules-unauth/rules-unauth.component';
import { LocateUnauthComponent } from './custom/locate-unauth/locate-unauth.component';
import { SignComponent } from './auth/sign/sign.component';
import { SocetService } from './PushServices/socet.service';
import { SearchBarComponent } from 'app/custom/search-bar/search-bar.component';
import { AdminReportService } from './core/admin-report.service';
import { AdminReportComponent } from './custom/admin-report/admin-report.component';
import { FilterComponent } from './custom/admin-report/components/filter/filter.component';
import { ReportComponent } from './custom/admin-report/components/report/report.component';
import { FilterPipe } from './core/pipes/filter.pipe';
import { WeightReportingComponent } from './custom/weight-reporting/weight-reporting.component';
import { FilterWeightComponent } from './custom/weight-reporting/components/filter-weight/filter-weight.component';
import { ReportWeightComponent } from './custom/weight-reporting/components/report-weight/report-weight.component';
import { BillingComponent } from './custom/billing/billing.component';
import { StripeComponent } from './core/stripe/stripe.component';
import { BillingService } from './core/billing.service';
import { TutorialComponent } from './core/tutorial/tutorial.component';
import { TooltipComponent } from './core/tooltip/tooltip.component';
import { OverageNotificationComponent } from "./core/overage-notification/overage-notification.component";
import { UserNotificationComponent } from "./core/user-notification/user-notification.component";
import { InvoicesComponent } from './custom/invoices/invoices.component';
import { InfoComponent } from './custom/info/info.component';
import { AuthFooterComponent } from './auth/auth-footer/auth-footer.component';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { AuthHeaderComponent } from './auth/auth-header/auth-header.component';
import { PreVetTravelFormComponent } from './custom/component/prevet_travel.component';
import { TreatmentSelectorComponent } from './custom/common/treatmentSelectorComponent.component';
import { IntraArticularAdditionalInfo } from './custom/common/intraArticularAdditionalInfo.component';
import { AusVerterinaryHistoryFormComponent } from './custom/common/aus-notes/ausVerterinaryHistoryForm.component';
import { AusDeclarationExecutionSubmissionComponent } from './custom/common/aus-notes/ausDeclarationExecutionSubmission.component';
import { AusVerterinaryInspectionFormComponent } from './custom/common/aus-notes/aus_veterinary_inspection_form.component';
import { AusRVTabsComponent } from './custom/aus-rv-tabs/aus-rv-tabs.component';
import { AusPreTravelTreatmentFields } from './custom/common/aus-treatment-fields/aus_preTravel_treatment_fields.component';
import { AusPreTravelVeterinaryHistoryDisClosureFormComponent } from './custom/common/aus-notes/aus_preTravel_veterinary_history_disclosure.component';
import { KaynaInsuranceComponent } from './custom/insurance/kayna/kayna.component';
import { InsuranceListingComponent } from './custom/insurance/insurancelisting.component';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

@NgModule({
  declarations: [
    AppComponent,
    HorsesComponent,
    HorseComponent,
    LoginComponent,
    SignComponent,
    ForgotComponent,
    NavComponent,
    HeaderXsComponent,
    HeaderComponent,
    RouteLoggedComponent,
    ProfileComponent,
    CatComponent,
    RuleBookComponent,
    YardsComponent,
    AboutComponent,
    ContactComponent,
    PaginationComponent,
    AllTreatmentsComponent,
    DateObjPipe,
    AgePipe,
    ActivityComponent,
    LoaderComponent,
    RulesUnauthComponent,
    LocateUnauthComponent,
    SearchBarComponent,
    AdminReportComponent,
    FilterComponent,
    ReportComponent,
    FilterPipe,
    WeightReportingComponent,
    FilterWeightComponent,
    ReportWeightComponent,
    TooltipComponent,
    BillingComponent,
    StripeComponent,
    TutorialComponent,
    OverageNotificationComponent,
    UserNotificationComponent,
    InvoicesComponent,
    InfoComponent,
    AuthFooterComponent,
    AuthHeaderComponent,
    KaynaInsuranceComponent,
    InsuranceListingComponent,
    PreVetTravelFormComponent,
    TreatmentSelectorComponent,
    IntraArticularAdditionalInfo,
    AusVerterinaryHistoryFormComponent,
    AusDeclarationExecutionSubmissionComponent,
    AusVerterinaryInspectionFormComponent,
    AusRVTabsComponent,
    AusPreTravelTreatmentFields,
    AusPreTravelVeterinaryHistoryDisClosureFormComponent
  ],
  imports: [
    BrowserModule,
    FormsModule,
    TextMaskModule,
    HttpModule,
    HttpClientModule,
    AppRoutingModule,
    LoginRoutingModule,
    ClipboardModule,
    SelectModule,
    NoopAnimationsModule,
    TabsModule.forRoot(),
    ButtonsModule.forRoot(),
    PopoverModule.forRoot(),
    ImageUploadModule.forRoot(),
    DatepickerModule.forRoot(),
    SimpleNotificationsModule.forRoot(),
    BsDropdownModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    ChartsModule,
    NgxStripeModule.forRoot(environment.stripeKey),
    ReactiveFormsModule,
    PdfViewerModule
  ],
  providers: [
    AppMemoryService,
    GetService,
    AdminReportService,
    DatePipe,
    SocetService,
    { provide: Http, useClass: AuthenticatedHttpService },
    BillingService,
    BillingComponent,
    DateObjPipe
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }



// WEBPACK FOOTER //
// /home/vsts/work/1/s/emr-webapp/src/app/app.module.ts